import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import JSZip from "jszip";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { FormattedMessage, useIntl } from "react-intl";
import {
  IconArrowDown,
  IconArrowUp,
  IconCalendar,
  IconDownload,
  IconPlus,
  IconTrash,
} from "@tabler/icons-react";
import DatePicker from "react-datepicker";
import Nav from "react-bootstrap/Nav";
import { Dropdown } from "react-bootstrap";
import {
  IonIcon,
  isPlatform,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  useIonToast,
  IonHeader,
  IonToolbar,
  IonButtons,
  IonButton,
  IonTitle,
  IonPage,
  IonContent,
  IonToggle,
  IonModal,
} from "@ionic/react";
import { closeCircle, chevronBackOutline } from "ionicons/icons";

// Components
import SkeletonText from "../SkeletonText";
import SkeletonLoader from "../SkeletonLoader";
import { Filter } from "./components/filters";
import FilterPill from "./components/filter-pill";
import Table from "./table";
import Modal from "./modal";
import RefundModal from "./refund-modal";
import List from "./list";
import CustomToggle from "../CustomToggle";

// Interfaces
import { Settings, PaymentsFilter } from "../../interfaces";

// Utils
import { parseDateForFilter } from "../../utils/moment";

// Api
import LeadsApi from "../../api/leads";
import IntegrationsApi from "../../api/integrations";
import AiApi from "../../api/ai";
import SettingsApi from "../../api/settings";
import PaymentItemsApi from "../../api/payment-items";
import { CURRENCIES } from "../integrations/constants";

// Store
import { usePaymentsStore } from "../../store/payments";
import { useAppStore } from "../../store/app";

// Styles
import styles from "./styles.module.css";
import headerStyles from "../../theme/header.module.css";

const isMobile = isPlatform("android") || isPlatform("ios");

const LayoutSkeleton = () => (
  <div className="row">
    <div className="col-md-12">
      <div className="flex flex-column w-100 h-100">
        <SkeletonText rows={20} />
      </div>
    </div>
  </div>
);

const download = (blob: Blob, filename = "data.csv") => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const escapeCSVValue = (value: any): string => {
  const stringValue = String(value ?? ""); // Maneja null/undefined
  const escaped = stringValue.replace(/"/g, '""'); // Escapa comillas dobles
  return `"${escaped}"`; // Encierra todo entre comillas dobles
};

const MobileTitle = ({
  backButton,
  id,
}: {
  backButton: string;
  id: string;
}) => {
  return (
    <IonHeader>
      <IonToolbar>
        <IonButtons slot="start">
          <IonButton
            routerLink="/settings"
            routerDirection="back"
            fill="clear"
            size="small"
            aria-label="Atrás"
          >
            <IonIcon slot="icon-only" icon={chevronBackOutline} />
          </IonButton>
        </IonButtons>
        <IonTitle>
          <FormattedMessage id={id} />
        </IonTitle>
      </IonToolbar>
    </IonHeader>
  );
};

interface PaymentItem {
  id: string;
  name: string;
  description: string;
  amount: number;
  currency: string;
  active: boolean;
  properties: ItemProperty[];
  createdAt?: string;
  updatedAt?: string;
}

type ItemPropertyType = "date" | "enum" | "text" | "number";

interface ItemProperty {
  fieldId: string;
  label: string;
  type: ItemPropertyType;
  required: boolean;
  enumOptions: string[];
  order: number;
  active: boolean;
}

interface ItemPropertyDraft extends ItemProperty {
  clientId: string;
}

type ItemPropertyPayload = Omit<ItemProperty, "enumOptions"> & {
  enumOptions?: string[];
};

const ITEM_PROPERTY_TYPES: ItemPropertyType[] = ["date", "enum", "text", "number"];

const createDraftProperty = (): ItemPropertyDraft => {
  const random = Math.random().toString(36).slice(2);
  return {
    clientId: `new_${Date.now()}_${random}`,
    fieldId: "",
    label: "",
    type: "text",
    required: false,
    enumOptions: [],
    order: 0,
    active: true,
  };
};

const Payments: React.FC<ILayoutProps> = (props: ILayoutProps) => {
  const intl = useIntl();

  const { locale } = intl;
  const history = useHistory();
  const location = useLocation();
  const [present] = useIonToast();
  const { setPayments, updatePaymentReconciled } = usePaymentsStore();
  const user = useAppStore((state) => state.user);
  const setSettings = useAppStore((state) => state.setSettings);
  const [stripeActionLoading, setStripeActionLoading] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [isOpenRefund, setIsOpenRefund] = useState(false);
  const [payment, setPayment] = useState<any>(null);
  const [depositPercentage, setDepositPercentage] = useState<number>(100);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [dateQuery, setDateQuery] = useState("");
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [filters, setFilters] = useState<PaymentsFilter>({
    companies: [],
    statuses: [],
  });
  const [items, setItems] = useState<PaymentItem[]>([]);
  const [isItemsLoading, setIsItemsLoading] = useState(false);
  const [itemActionLoading, setItemActionLoading] = useState("");
  const [isItemModalOpen, setIsItemModalOpen] = useState(false);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [itemName, setItemName] = useState("");
  const [itemDescription, setItemDescription] = useState("");
  const [itemPriceInput, setItemPriceInput] = useState("");
  const [itemCurrency, setItemCurrency] = useState("USD");
  const [itemActive, setItemActive] = useState(true);
  const [itemProperties, setItemProperties] = useState<ItemPropertyDraft[]>([]);
  const [enumOptionDraftByProperty, setEnumOptionDraftByProperty] = useState<
    Record<string, string>
  >({});
  const isEligible = Boolean(props.settings.paymentsEligible);
  const connectedAccount = props.settings.payments?.connectedAccount || "";
  const paymentsActive = Boolean(props.settings.payments?.active);
  const itemsActive = props.settings.payments?.itemsActive !== false;
  const bookingControlsEnabled = Boolean(
    props.settings.payments?.bookingControlsEnabled,
  );
  const isStripeConnected = Boolean(connectedAccount);
  const normalizedPath =
    location.pathname.length > 1
      ? location.pathname.replace(/\/+$/, "")
      : location.pathname;
  const itemCurrencyOptions = CURRENCIES.includes(itemCurrency)
    ? CURRENCIES
    : [itemCurrency, ...CURRENCIES];

  const { isLoading: isTotalsLoading, data: totalsData } = useQuery({
    queryFn: () => LeadsApi.getPaymentsTotals(),
    queryKey: ["getPaymentsTotals"],
    enabled: isEligible && isStripeConnected,
    refetchOnWindowFocus: false,
  });

  const hasPayments = Number(totalsData?.totalCount || 0) > 0;
  const shouldShowSettingsLanding =
    !isStripeConnected ||
    (!bookingControlsEnabled && !isTotalsLoading && !hasPayments);
  const shouldHideViewNavigation = shouldShowSettingsLanding && !isStripeConnected;
  const showLandingHero = shouldShowSettingsLanding && !isStripeConnected;
  const activeView: "payments" | "stripe" | "booking" | "items" =
    normalizedPath === "/payments/items"
      ? "items"
      : normalizedPath === "/payments/booking"
        ? bookingControlsEnabled
          ? "booking"
          : "stripe"
        : normalizedPath === "/payments/stripe" || normalizedPath === "/payments/settings"
          ? "stripe"
          : shouldShowSettingsLanding
            ? "stripe"
            : "payments";

  const {
    data,
    fetchNextPage,
    hasNextPage,
    hasPreviousPage,
    isFetching,
    isFetchingNextPage,
    isFetchingPreviousPage,
  } = useInfiniteQuery({
    queryKey: ["getLeads", dateQuery, filters],
    enabled: isEligible && isStripeConnected && hasPayments,
    queryFn: ({
      pageParam,
    }: {
      pageParam?: { cursor: string | null; direction: string | null };
    }) => {
      const { cursor = null, direction = null } = pageParam ?? {};
      return LeadsApi.getPayments({
        date: dateQuery,
        cursor,
        direction,
        filters,
      });
    },
    refetchOnWindowFocus: false,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    getPreviousPageParam: (_, pages) => pages[pages.length - 1].previousCursor,
  });

  useEffect(() => {
    if (data) {
      setPayments(
        !isMobile
          ? data?.pages[data?.pages.length - 1].payments
          : data?.pages.flatMap((page) => page.payments),
      );
    }
  }, [data]);

  useEffect(() => {
    const savedPercentage = Number(props.settings.payments?.percentage);
    if (
      Number.isFinite(savedPercentage) &&
      savedPercentage >= 0.01 &&
      savedPercentage <= 1
    ) {
      setDepositPercentage(Math.round(savedPercentage * 100));
      return;
    }

    setDepositPercentage(100);
  }, [props.settings.payments?.percentage]);

  useEffect(() => {
    if (!isEligible) return;
    void loadPaymentItems();
  }, [isEligible]);

  const handleExport = async () => {
    setExportLoading(true);
    const { payments } = await LeadsApi.getPaymentsExport();
    setExportLoading(false);
    const zip = new JSZip();
    if (payments?.length) {
      const rows = payments.map((row: any[]) =>
        row.map(escapeCSVValue).join(","),
      );
      zip.file("leads.csv", rows.join("\n"));
    }

    const blob = await zip.generateAsync({ type: "blob" });
    download(blob, "export-payments.zip");
  };

  const onChangeDates = (dates: [Date, Date]) => {
    const [start, end] = dates;
    if (start && end) {
      setDateQuery(
        () => `${parseDateForFilter(start)},${parseDateForFilter(end)}`,
      );
    }
    setStartDate(start);
    setEndDate(end);
  };

  const handleRefundFunds = async (payment: any, amount: number) => {
    if (payment?.id) {
      const { data, err } = await LeadsApi.postRefundFunds({
        paymentId: payment?.id,
        amount,
      });
      if (data) {
        return present({
          message: intl.formatMessage({ id: "common.success" }),
          duration: 10000,
          color: "success",
          position: "top",
        });
      }
      return present({
        message: err.error,
        duration: 10000,
        color: "danger",
        position: "top",
      });
    }
  };

  const handleTransfFunds = async (payment: any) => {
    if (payment?.id) {
      const { data, err } = await LeadsApi.postTransferFunds({
        paymentId: payment?.id,
      });
      if (data) {
        return present({
          message: intl.formatMessage({ id: "common.success" }),
          duration: 10000,
          color: "success",
          position: "top",
        });
      }
      return present({
        message: err.err,
        duration: 10000,
        color: "danger",
        position: "top",
      });
    }
  };

  const handleHydrateBalance = async (payment: any) => {
    if (payment?.id) {
      const { data, err } = await LeadsApi.postHydrateBalance({
        paymentId: payment?.id,
      });
      if (data) {
        return present({
          message: intl.formatMessage({ id: "common.success" }),
          duration: 10000,
          color: "success",
          position: "top",
        });
      }
      return present({
        message: err.err,
        duration: 10000,
        color: "danger",
        position: "top",
      });
    }
  };

  const handleToggleReconciled = (payment: any, reconciled: boolean) => {
    if (!payment?.id) return;

    const reconciledAt = reconciled ? new Date().toISOString() : null;
    const reconciledBy = reconciled ? user?.email || null : null;

    // Optimistic update: trust backend persistence and avoid list refetches.
    updatePaymentReconciled({
      id: payment.id,
      reconciled,
      reconciledAt,
      reconciledBy,
    });

    void LeadsApi.postReconcilePayment({
      paymentId: payment.id,
      reconciled,
    }).then(({ err }) => {
      if (err) {
        console.error("Failed to update reconciliation", err);
      }
    });
  };

  const openPaymentModal = (payment: any) => {
    // Logic to open payment modal
    setIsOpen(true);
    setPayment(payment);
  };

  const openConversation = (payment: any) => {
    const contactId = payment.contact?.id;

    if (!contactId) {
      return present({
        message: intl.formatMessage({ id: "common.error" }),
        duration: 10000,
        color: "danger",
        position: "top",
      });
    }
    if (isMobile) {
      history.push(`/chat/${encodeURIComponent(contactId)}`);
    } else {
      history.push(`/chats/${encodeURIComponent(contactId)}`);
    }
  };

  const filterPillTotal = (
    <FilterPill
      isMobile={isMobile}
      name={intl.formatMessage({ id: "leads.stats.total" })}
      count={totalsData?.totalCount}
      active={
        filters.statuses.length === 0 && startDate === null && endDate === null
      }
      onClick={() => {
        setFilters({
          statuses: [],
          companies: [],
        });
        setDateQuery("");
        setStartDate(null);
        setEndDate(null);
      }}
    />
  );

  const filterPillPaid = (
    <FilterPill
      isMobile={isMobile}
      name={intl.formatMessage({ id: `leads.payments.paid` })}
      count={totalsData?.paidTotal}
      active={filters.statuses.length === 1 && filters.statuses[0] === "paid"}
      onClick={() => {
        setFilters({
          ...filters,
          statuses: ["paid"],
          companies: [],
        });
        setDateQuery("");
        setStartDate(null);
        setEndDate(null);
      }}
    />
  );

  const { nextCursor, previousCursor } =
    data?.pages[data.pageParams.length - 1] || {};

  const getErrorMessage = (err: any) =>
    err?.[intl.locale] || err?.error || intl.formatMessage({ id: "common.tryItLater" });

  const formatMinorUnits = (amount: number, currency: string) => {
    const code = (currency || "USD").toUpperCase();
    return `${(Number(amount || 0) / 100).toFixed(2)} ${code}`;
  };

  const itemPropertyTypeLabelId = (type: ItemPropertyType) =>
    `payments.items.propertyType.${type}`;

  const normalizeDraftPropertiesForPayload = (
    properties: ItemPropertyDraft[],
  ): ItemPropertyPayload[] => {
    return properties.map((property, index) => {
      const enumOptions = (property.enumOptions || [])
        .map((option) => String(option || "").trim())
        .filter(Boolean);

      const baseProperty = {
        fieldId: String(property.fieldId || "").trim(),
        label: String(property.label || "").trim(),
        type: property.type,
        required: Boolean(property.required),
        order: index,
        active: property.active !== false,
      };

      if (property.type === "enum") {
        return {
          ...baseProperty,
          enumOptions,
        };
      }

      return baseProperty;
    });
  };

  const resetItemForm = () => {
    setEditingItemId(null);
    setItemName("");
    setItemDescription("");
    setItemPriceInput("");
    setItemCurrency("USD");
    setItemActive(true);
    setItemProperties([]);
    setEnumOptionDraftByProperty({});
  };

  const openCreateItemModal = () => {
    resetItemForm();
    setIsItemModalOpen(true);
  };

  const closeItemModal = () => {
    setIsItemModalOpen(false);
    resetItemForm();
  };

  const hydratePropertiesForEdit = (
    properties: ItemProperty[] = [],
  ): ItemPropertyDraft[] => {
    return properties
      .slice()
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map((property, index) => ({
        clientId:
          property.fieldId ||
          `existing_${index}_${Math.random().toString(36).slice(2)}`,
        fieldId: property.fieldId || "",
        label: property.label || "",
        type: (property.type || "text") as ItemPropertyType,
        required: Boolean(property.required),
        enumOptions: Array.isArray(property.enumOptions)
          ? property.enumOptions.filter(Boolean)
          : [],
        order:
          Number.isInteger(property.order) && property.order >= 0
            ? property.order
            : index,
        active: property.active !== false,
      }));
  };

  const updateDraftProperty = (
    clientId: string,
    updater: (property: ItemPropertyDraft) => ItemPropertyDraft,
  ) => {
    setItemProperties((prev) =>
      prev.map((property) =>
        property.clientId === clientId ? updater(property) : property,
      ),
    );
  };

  const addDraftProperty = () => {
    setItemProperties((prev) => [...prev, createDraftProperty()]);
  };

  const removeDraftProperty = (clientId: string) => {
    setItemProperties((prev) =>
      prev.filter((property) => property.clientId !== clientId),
    );
    setEnumOptionDraftByProperty((prev) => {
      if (!(clientId in prev)) return prev;
      const next = { ...prev };
      delete next[clientId];
      return next;
    });
  };

  const moveDraftProperty = (clientId: string, direction: "up" | "down") => {
    setItemProperties((prev) => {
      const index = prev.findIndex((property) => property.clientId === clientId);
      if (index < 0) return prev;
      const target = direction === "up" ? index - 1 : index + 1;
      if (target < 0 || target >= prev.length) return prev;

      const next = [...prev];
      const [item] = next.splice(index, 1);
      next.splice(target, 0, item);
      return next;
    });
  };

  const addEnumOption = (clientId: string) => {
    const option = String(enumOptionDraftByProperty[clientId] || "").trim();
    if (!option) return;

    updateDraftProperty(clientId, (property) => {
      if (property.enumOptions.includes(option)) return property;
      return {
        ...property,
        enumOptions: [...property.enumOptions, option],
      };
    });

    setEnumOptionDraftByProperty((prev) => ({
      ...prev,
      [clientId]: "",
    }));
  };

  const removeEnumOption = (clientId: string, option: string) => {
    updateDraftProperty(clientId, (property) => ({
      ...property,
      enumOptions: property.enumOptions.filter((value) => value !== option),
    }));
  };

  const loadPaymentItems = async () => {
    if (!isEligible) return;
    setIsItemsLoading(true);
    const { data, err } = await PaymentItemsApi.list();
    setIsItemsLoading(false);

    if (err) {
      return present({
        message: getErrorMessage(err),
        duration: 6000,
        color: "danger",
        position: "top",
      });
    }

    setItems(
      (data?.items || []).map((item: any) => ({
        ...item,
        properties: Array.isArray(item.properties) ? item.properties : [],
      })),
    );
  };

  const refreshSettings = async () => {
    const { data } = await SettingsApi.get();
    if (data?.settings) {
      setSettings(data.settings);
    }
  };

  const savePaymentItem = async () => {
    const name = itemName.trim();
    const description = itemDescription.trim();
    const amountDecimal = Number(itemPriceInput);
    const amount = Math.round(amountDecimal * 100);
    const currency = itemCurrency.trim().toUpperCase();
    const validCurrency = CURRENCIES.includes(currency);
    const properties = normalizeDraftPropertiesForPayload(itemProperties);
    const fieldIds = new Set<string>();
    const propertiesAreValid = properties.every((property) => {
      if (!property.label) return false;
      if (!ITEM_PROPERTY_TYPES.includes(property.type)) return false;
      if (property.fieldId) {
        if (fieldIds.has(property.fieldId)) return false;
        fieldIds.add(property.fieldId);
      }
      if (property.type === "enum") {
        return (property.enumOptions || []).length > 0;
      }
      return true;
    });

    if (
      !name ||
      !Number.isFinite(amountDecimal) ||
      amount < 1 ||
      !validCurrency ||
      !propertiesAreValid
    ) {
      return present({
        message: intl.formatMessage({ id: "payments.items.validationError" }),
        duration: 5000,
        color: "danger",
        position: "top",
      });
    }

    setItemActionLoading("save");
    const response = editingItemId
      ? await PaymentItemsApi.update({
          itemId: editingItemId,
          name,
          description,
          amount,
          currency,
          active: itemActive,
          properties,
        } as any)
      : await PaymentItemsApi.create({
          name,
          description,
          amount,
          currency,
          active: itemActive,
          properties,
        });
    setItemActionLoading("");

    if (response.err) {
      return present({
        message: getErrorMessage(response.err),
        duration: 6000,
        color: "danger",
        position: "top",
      });
    }

    closeItemModal();
    await loadPaymentItems();
    await refreshSettings();
    return present({
      message: intl.formatMessage({ id: "common.success" }),
      duration: 4000,
      color: "success",
      position: "top",
    });
  };

  const editPaymentItem = (item: PaymentItem) => {
    setEditingItemId(item.id);
    setItemName(item.name || "");
    setItemDescription(item.description || "");
    setItemPriceInput((Number(item.amount || 0) / 100).toFixed(2));
    setItemCurrency((item.currency || "USD").toUpperCase());
    setItemActive(Boolean(item.active));
    setItemProperties(hydratePropertiesForEdit(item.properties || []));
    setEnumOptionDraftByProperty({});
    setIsItemModalOpen(true);
  };

  const deactivatePaymentItem = async (item: PaymentItem) => {
    setItemActionLoading(`deactivate:${item.id}`);
    const response = await PaymentItemsApi.update({
      itemId: item.id,
      active: false,
    } as any);
    setItemActionLoading("");
    if (response.err) {
      return present({
        message: getErrorMessage(response.err),
        duration: 6000,
        color: "danger",
        position: "top",
      });
    }
    await loadPaymentItems();
    await refreshSettings();
  };

  const deletePaymentItem = async (item: PaymentItem) => {
    const confirmed = window.confirm(
      intl.formatMessage({ id: "payments.items.deleteConfirm" }),
    );
    if (!confirmed) return;

    setItemActionLoading(`delete:${item.id}`);
    const response = await PaymentItemsApi.remove({ itemId: item.id });
    setItemActionLoading("");
    if (response.err) {
      return present({
        message: getErrorMessage(response.err),
        duration: 6000,
        color: "danger",
        position: "top",
      });
    }

    if (editingItemId === item.id) {
      closeItemModal();
    }
    await loadPaymentItems();
    await refreshSettings();
    return present({
      message: intl.formatMessage({ id: "common.success" }),
      duration: 4000,
      color: "success",
      position: "top",
    });
  };

  const connectStripeExpress = async () => {
    setStripeActionLoading("connect");
    const { data, err } = await IntegrationsApi.stripeConnectLink();
    setStripeActionLoading("");

    if (err || !data?.url) {
      return present({
        message: getErrorMessage(err),
        duration: 6000,
        color: "danger",
        position: "top",
      });
    }

    window.location.href = data.url;
  };

  const openStripeExpress = async () => {
    setStripeActionLoading("open");
    const { data, err } = await IntegrationsApi.stripeLoginLink();
    setStripeActionLoading("");

    if (err || !data?.url) {
      return present({
        message: getErrorMessage(err),
        duration: 6000,
        color: "danger",
        position: "top",
      });
    }

    window.open(data.url, "_blank");
  };

  const disconnectStripeExpress = async () => {
    setStripeActionLoading("disconnect");
    const { data, err } = await IntegrationsApi.stripeDisconnect();
    setStripeActionLoading("");

    if (err || !data?.success) {
      return present({
        message: getErrorMessage(err),
        duration: 6000,
        color: "danger",
        position: "top",
      });
    }

    await refreshSettings();
    return present({
      message: intl.formatMessage({ id: "payments.stripe.disconnectSuccess" }),
      duration: 5000,
      color: "success",
      position: "top",
    });
  };

  const toggleAiBookingsPayments = async (active: boolean) => {
    setStripeActionLoading("toggle");
    const { err } = await AiApi.putAiSettingsByField("payments.active", active);
    setStripeActionLoading("");

    if (err) {
      return present({
        message: getErrorMessage(err),
        duration: 6000,
        color: "danger",
        position: "top",
      });
    }

    await refreshSettings();
  };

  const toggleItemPayments = async (active: boolean) => {
    setStripeActionLoading("itemsToggle");
    const { err } = await AiApi.putAiSettingsByField(
      "payments.itemsActive",
      active,
    );
    setStripeActionLoading("");

    if (err) {
      return present({
        message: getErrorMessage(err),
        duration: 6000,
        color: "danger",
        position: "top",
      });
    }

    await refreshSettings();
  };

  const saveDepositPercentage = async () => {
    const sanitized = Math.max(1, Math.min(100, Math.round(depositPercentage)));

    setStripeActionLoading("percentage");
    const { err } = await AiApi.putAiSettingsByField(
      "payments.percentage",
      sanitized / 100,
    );
    setStripeActionLoading("");

    if (err) {
      return present({
        message: getErrorMessage(err),
        duration: 6000,
        color: "danger",
        position: "top",
      });
    }

    setDepositPercentage(sanitized);
    await refreshSettings();
    return present({
      message: intl.formatMessage({ id: "common.success" }),
      duration: 4000,
      color: "success",
      position: "top",
    });
  };

  const stripeCard = (
    <div
      className={`visito-card mb-3 ${shouldShowSettingsLanding ? styles.landingCard : ""}`}
    >
      <div className="visito-card-body">
        {showLandingHero ? (
          <div className={styles.landingHero}>
            <h2 className={styles.landingHeroTitle}>
              <FormattedMessage id="payments.stripe.landingHeadline" />
            </h2>
            <p className={styles.landingHeroSubtitle}>
              <FormattedMessage id="payments.stripe.landingSubheadline" />
            </p>
            <div className="d-flex gap-2 flex-wrap justify-content-center">
              <a
                className="btn btn-visito-light btn-sm"
                href="https://docs.visitoai.com/product-guides/product/in-chat-payments"
                target="_blank"
                rel="noreferrer"
              >
                <FormattedMessage id="payments.stripe.docsInChatPayments" />
              </a>
              <a
                className="btn btn-visito-light btn-sm"
                href="https://docs.visitoai.com/product-guides/product/items-catalog"
                target="_blank"
                rel="noreferrer"
              >
                <FormattedMessage id="payments.stripe.docsItemsCatalog" />
              </a>
            </div>
          </div>
        ) : (
          <h3 className="fs-3 mb-2">
            <FormattedMessage id="payments.stripe.title" />
          </h3>
        )}
        {!isStripeConnected ? (
          <>
            {!showLandingHero ? (
              <p className="fs-3 color-step-600 mb-3">
                <FormattedMessage id="payments.stripe.connectMessage" />
              </p>
            ) : null}
            <button
              className={`btn-visito-primary btn btn-sm ${
                showLandingHero ? "mx-auto d-block" : ""
              }`}
              onClick={connectStripeExpress}
              disabled={stripeActionLoading === "connect"}
            >
              {stripeActionLoading === "connect" ? (
                <FormattedMessage id="common.loading" />
              ) : (
                <FormattedMessage id="payments.stripe.connect" />
              )}
            </button>
          </>
        ) : (
          <>
            <p className="fs-3 color-step-600 mb-2">
              <FormattedMessage
                id="payments.stripe.connectedAccount"
                values={{ account: connectedAccount }}
              />
            </p>
            <div className="d-flex gap-2 flex-wrap">
              <button
                className="btn-visito-primary btn btn-sm"
                onClick={openStripeExpress}
                disabled={stripeActionLoading === "open"}
              >
                {stripeActionLoading === "open" ? (
                  <FormattedMessage id="common.loading" />
                ) : (
                  <FormattedMessage id="payments.stripe.openExpress" />
                )}
              </button>
              <button
                className="btn btn-visito-light btn-sm"
                onClick={disconnectStripeExpress}
                disabled={stripeActionLoading === "disconnect"}
              >
                {stripeActionLoading === "disconnect" ? (
                  <FormattedMessage id="common.loading" />
                ) : (
                  <FormattedMessage id="payments.stripe.disconnect" />
                )}
              </button>
            </div>
          </>
        )}

      </div>
    </div>
  );

  const bookingSettingsCard = (
    <div className="visito-card mb-3">
      <div className="visito-card-body">
        <h3 className="fs-3 mb-2">
          <FormattedMessage id="payments.tabs.bookingSettings" />
        </h3>
        {!isStripeConnected ? (
          <p className="fs-3 color-step-600 mb-0">
            <FormattedMessage id="payments.booking.connectStripeFirst" />
          </p>
        ) : (
          <>
            <div className="d-flex align-items-center gap-2 mb-3">
              <IonToggle
                mode="ios"
                className="ios"
                checked={paymentsActive}
                disabled={stripeActionLoading === "toggle"}
                onIonChange={(event) =>
                  toggleAiBookingsPayments(event.detail.checked)
                }
              ></IonToggle>
              <span className="fs-3 color-step-700">
                <FormattedMessage id="payments.stripe.payoutsToggle" />
              </span>
            </div>
            <div className="mb-0">
              <label className="form-label fs-3 color-step-700 mb-1">
                <FormattedMessage id="payments.stripe.depositPercentageLabel" />
              </label>
              <p className="fs-2 color-step-600 mb-2">
                <FormattedMessage id="payments.stripe.depositPercentageHelp" />
              </p>
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <div
                  className="input-group input-group-sm"
                  style={{ maxWidth: "160px" }}
                >
                  <input
                    type="number"
                    min={1}
                    max={100}
                    step={1}
                    className="form-control"
                    value={depositPercentage}
                    onChange={(event) => {
                      const value = Number(event.currentTarget.value);
                      if (!Number.isFinite(value)) {
                        setDepositPercentage(1);
                        return;
                      }
                      setDepositPercentage(Math.max(1, Math.min(100, value)));
                    }}
                  />
                  <span className="input-group-text">%</span>
                </div>
                <button
                  className="btn btn-visito-light btn-sm"
                  disabled={stripeActionLoading === "percentage"}
                  onClick={saveDepositPercentage}
                >
                  {stripeActionLoading === "percentage" ? (
                    <FormattedMessage id="common.loading" />
                  ) : (
                    <FormattedMessage id="common.save" />
                  )}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );

  const itemFormModal = (
    <IonModal isOpen={isItemModalOpen} onWillDismiss={closeItemModal}>
      <IonHeader>
        <IonToolbar>
          <IonTitle>
            <FormattedMessage
              id={editingItemId ? "payments.items.update" : "payments.items.create"}
            />
          </IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={closeItemModal}>
              <FormattedMessage id="common.cancel" />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <div className="p-3 p-md-4">
          <form
            onSubmit={(event) => {
              event.preventDefault();
              void savePaymentItem();
            }}
          >
            <div className="row g-2">
              <div className="col-md-4">
                <label className="form-label fs-3 color-step-700 mb-1">
                  <FormattedMessage id="payments.items.name" />
                </label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={itemName}
                  onChange={(event) => setItemName(event.currentTarget.value)}
                  placeholder={intl.formatMessage({
                    id: "payments.items.namePlaceholder",
                  })}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label fs-3 color-step-700 mb-1">
                  <FormattedMessage id="payments.items.price" />
                </label>
                <input
                  type="number"
                  min={0.01}
                  step={0.01}
                  className="form-control form-control-sm"
                  value={itemPriceInput}
                  onChange={(event) => setItemPriceInput(event.currentTarget.value)}
                  placeholder={intl.formatMessage({
                    id: "payments.items.pricePlaceholder",
                  })}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label fs-3 color-step-700 mb-1">
                  <FormattedMessage id="payments.items.currency" />
                </label>
                <select
                  className="form-select form-select-sm"
                  value={itemCurrency}
                  onChange={(event) => setItemCurrency(event.currentTarget.value)}
                >
                  {itemCurrencyOptions.map((currencyCode) => (
                    <option key={currencyCode} value={currencyCode}>
                      {currencyCode}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-12">
                <label className="form-label fs-3 color-step-700 mb-1">
                  <FormattedMessage id="payments.items.description" />
                </label>
                <textarea
                  className="form-control form-control-sm"
                  rows={2}
                  value={itemDescription}
                  onChange={(event) => setItemDescription(event.currentTarget.value)}
                  placeholder={intl.formatMessage({
                    id: "payments.items.descriptionPlaceholder",
                  })}
                />
              </div>
            </div>

            <hr className="my-3" />
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div>
                <h4 className="fs-3 mb-1">
                  <FormattedMessage id="payments.items.propertiesTitle" />
                </h4>
                <p className="fs-2 color-step-600 mb-0">
                  <FormattedMessage id="payments.items.propertiesSubtitle" />
                </p>
              </div>
              <button
                className="btn btn-visito-light btn-sm d-flex align-items-center gap-1"
                onClick={addDraftProperty}
                type="button"
              >
                <IconPlus size={14} />
                <FormattedMessage id="payments.items.addProperty" />
              </button>
            </div>

            {itemProperties.length === 0 ? (
              <p className="fs-3 color-step-600 mb-3">
                <FormattedMessage id="payments.items.propertiesEmpty" />
              </p>
            ) : (
              <div className="d-flex flex-column gap-2 mb-3">
                {itemProperties.map((property, index) => (
                  <div key={property.clientId} className="border rounded p-2">
                    <div className="row g-2">
                      <div className="col-md-4">
                        <label className="form-label fs-3 color-step-700 mb-1">
                          <FormattedMessage id="payments.items.propertyLabel" />
                        </label>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={property.label}
                          onChange={(event) => {
                            const value = event.currentTarget.value;
                            updateDraftProperty(property.clientId, (current) => ({
                              ...current,
                              label: value,
                            }));
                          }}
                          placeholder={intl.formatMessage({
                            id: "payments.items.propertyLabelPlaceholder",
                          })}
                        />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label fs-3 color-step-700 mb-1">
                          <FormattedMessage id="payments.items.propertyType" />
                        </label>
                        <select
                          className="form-select form-select-sm"
                          value={property.type}
                          onChange={(event) => {
                            const value = event.currentTarget
                              .value as ItemPropertyType;
                            updateDraftProperty(property.clientId, (current) => ({
                              ...current,
                              type: value,
                              enumOptions:
                                value === "enum" ? current.enumOptions : [],
                            }));
                          }}
                        >
                          {ITEM_PROPERTY_TYPES.map((type) => (
                            <option key={type} value={type}>
                              {intl.formatMessage({
                                id: itemPropertyTypeLabelId(type),
                              })}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-3 d-flex align-items-end">
                        <div className="form-check m-0">
                          <input
                            id={`property-required-${property.clientId}`}
                            className="form-check-input"
                            type="checkbox"
                            checked={property.required}
                            onChange={(event) => {
                              const checked = event.currentTarget.checked;
                              updateDraftProperty(property.clientId, (current) => ({
                                ...current,
                                required: checked,
                              }));
                            }}
                          />
                          <label
                            className="form-check-label fs-3 color-step-700"
                            htmlFor={`property-required-${property.clientId}`}
                          >
                            <FormattedMessage id="payments.items.propertyRequired" />
                          </label>
                        </div>
                      </div>
                      <div className="col-md-2 d-flex align-items-end justify-content-end">
                        <div className="d-flex gap-1">
                          <button
                            className="btn btn-visito-light btn-sm"
                            type="button"
                            onClick={() => moveDraftProperty(property.clientId, "up")}
                            disabled={index === 0}
                          >
                            <IconArrowUp size={14} />
                          </button>
                          <button
                            className="btn btn-visito-light btn-sm"
                            type="button"
                            onClick={() =>
                              moveDraftProperty(property.clientId, "down")
                            }
                            disabled={index === itemProperties.length - 1}
                          >
                            <IconArrowDown size={14} />
                          </button>
                          <button
                            className="btn btn-visito-light btn-sm"
                            type="button"
                            onClick={() => removeDraftProperty(property.clientId)}
                          >
                            <IconTrash size={14} />
                          </button>
                        </div>
                      </div>
                    </div>

                    {property.type === "enum" ? (
                      <div className="mt-2">
                        <label className="form-label fs-3 color-step-700 mb-1">
                          <FormattedMessage id="payments.items.propertyEnumOptions" />
                        </label>
                        <div className="d-flex gap-2 align-items-center">
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            value={enumOptionDraftByProperty[property.clientId] || ""}
                            onChange={(event) => {
                              const value = event.currentTarget.value;
                              setEnumOptionDraftByProperty((prev) => ({
                                ...prev,
                                [property.clientId]: value,
                              }));
                            }}
                            placeholder={intl.formatMessage({
                              id: "payments.items.propertyEnumOptionPlaceholder",
                            })}
                          />
                          <button
                            className="btn btn-visito-light btn-sm"
                            type="button"
                            onClick={() => addEnumOption(property.clientId)}
                          >
                            <FormattedMessage id="payments.items.propertyAddOption" />
                          </button>
                        </div>
                        <div className="d-flex gap-2 flex-wrap mt-2">
                          {property.enumOptions.map((option) => (
                            <span
                              key={`${property.clientId}-${option}`}
                              className="badge text-bg-light d-flex align-items-center gap-2"
                            >
                              {option}
                              <button
                                className="btn btn-link p-0 m-0 text-reset"
                                type="button"
                                onClick={() =>
                                  removeEnumOption(property.clientId, option)
                                }
                              >
                                <IconTrash size={12} />
                              </button>
                            </span>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            )}

            <div className="d-flex gap-2 align-items-center flex-wrap mt-2 mb-3">
              <div className="form-check m-0">
                <input
                  id="payment-item-active"
                  className="form-check-input"
                  type="checkbox"
                  checked={itemActive}
                  onChange={(event) => setItemActive(event.currentTarget.checked)}
                />
                <label
                  className="form-check-label fs-3 color-step-700"
                  htmlFor="payment-item-active"
                >
                  <FormattedMessage id="payments.items.active" />
                </label>
              </div>
            </div>

            <div className="d-flex justify-content-end gap-2 flex-wrap">
              <button className="btn btn-visito-light btn-sm" type="button" onClick={closeItemModal}>
                <FormattedMessage id="common.cancel" />
              </button>
              <button
                className="btn-visito-primary btn btn-sm"
                type="submit"
                disabled={itemActionLoading === "save"}
              >
                {itemActionLoading === "save" ? (
                  <FormattedMessage id="common.loading" />
                ) : editingItemId ? (
                  <FormattedMessage id="payments.items.update" />
                ) : (
                  <FormattedMessage id="payments.items.create" />
                )}
              </button>
            </div>
          </form>
        </div>
      </IonContent>
    </IonModal>
  );

  const itemsCard = (
    <div className={`visito-card mb-3 ${styles.itemsCard}`}>
      <div className="visito-card-body">
        <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
          <div>
            <h3 className="fs-3 mb-2">
              <FormattedMessage id="payments.items.title" />
            </h3>
            <p className="fs-3 color-step-600 mb-0">
              <FormattedMessage id="payments.items.subtitle" />
            </p>
          </div>
          <div className="d-flex gap-2 align-items-center">
            <a
              className="btn btn-visito-light btn-sm"
              href="https://docs.visitoai.com/product-guides/product/items-catalog"
              target="_blank"
              rel="noreferrer"
            >
              <FormattedMessage id="payments.items.docs" />
            </a>
            <button
              className="btn-visito-primary btn btn-sm d-flex align-items-center gap-1"
              onClick={openCreateItemModal}
              type="button"
            >
              <IconPlus size={14} />
              <FormattedMessage id="payments.items.create" />
            </button>
          </div>
        </div>
        {isStripeConnected ? (
          <div className="mb-3">
            <div className="d-flex align-items-center gap-2">
              <IonToggle
                mode="ios"
                className="ios"
                checked={itemsActive}
                disabled={stripeActionLoading === "itemsToggle"}
                onIonChange={(event) => toggleItemPayments(event.detail.checked)}
              ></IonToggle>
              <span className="fs-3 color-step-700">
                <FormattedMessage id="payments.stripe.itemsToggleLabel" />
              </span>
            </div>
            <p className="fs-2 color-step-600 mb-0">
              <FormattedMessage id="payments.stripe.itemsToggleHelp" />
            </p>
          </div>
        ) : null}

        {isItemsLoading ? (
          <p className="fs-3 color-step-600 mb-0">
            <FormattedMessage id="common.loading" />
          </p>
        ) : items.length === 0 ? (
          <p className="fs-3 color-step-600 mb-0">
            <FormattedMessage id="payments.items.empty" />
          </p>
        ) : (
          <div className={styles.itemsTableWrapper}>
            <table className="table table-sm align-middle mb-0">
              <thead>
                <tr>
                  <th>
                    <FormattedMessage id="payments.items.name" />
                  </th>
                  <th>
                    <FormattedMessage id="payments.items.price" />
                  </th>
                  <th>
                    <FormattedMessage id="leads.payments.status" />
                  </th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {items.map((item, index) => (
                  <tr key={item.id}>
                    <td>
                      <div className="fw-semibold">{item.name}</div>
                      {item.description ? (
                        <div className="text-muted fs-4">{item.description}</div>
                      ) : null}
                      {item.properties?.length ? (
                        <div className="text-muted fs-4">
                          {intl.formatMessage(
                            { id: "payments.items.propertiesCount" },
                            { count: item.properties.length },
                          )}
                        </div>
                      ) : null}
                    </td>
                    <td>{formatMinorUnits(item.amount, item.currency)}</td>
                    <td>
                      {item.active ? (
                        <FormattedMessage id="payments.items.statusActive" />
                      ) : (
                        <FormattedMessage id="payments.items.statusInactive" />
                      )}
                    </td>
                    <td>
                      <div className="d-flex justify-content-end">
                        <Dropdown
                          style={{ marginLeft: "12px" }}
                          drop={index >= items.length - 2 ? "up" : "down"}
                        >
                          <Dropdown.Toggle
                            id={`items-actions-${item.id}`}
                            as={CustomToggle}
                            title="options"
                          >
                            Custom toggle
                          </Dropdown.Toggle>
                          <Dropdown.Menu align="end" popperConfig={{ strategy: "fixed" }}>
                            <Dropdown.Item
                              onClick={(event) => {
                                event.stopPropagation();
                                editPaymentItem(item);
                                setTimeout(() => {
                                  document.body.click();
                                }, 0);
                              }}
                            >
                              <FormattedMessage id="payments.items.edit" />
                            </Dropdown.Item>
                            {item.active ? (
                              <Dropdown.Item
                                disabled={itemActionLoading === `deactivate:${item.id}`}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  void deactivatePaymentItem(item);
                                  setTimeout(() => {
                                    document.body.click();
                                  }, 0);
                                }}
                              >
                                {itemActionLoading === `deactivate:${item.id}` ? (
                                  <FormattedMessage id="common.loading" />
                                ) : (
                                  <FormattedMessage id="payments.items.deactivate" />
                                )}
                              </Dropdown.Item>
                            ) : null}
                            <Dropdown.Item
                              disabled={itemActionLoading === `delete:${item.id}`}
                              onClick={(event) => {
                                event.stopPropagation();
                                void deletePaymentItem(item);
                                setTimeout(() => {
                                  document.body.click();
                                }, 0);
                              }}
                            >
                              {itemActionLoading === `delete:${item.id}` ? (
                                <FormattedMessage id="common.loading" />
                              ) : (
                                <FormattedMessage id="payments.items.delete" />
                              )}
                            </Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  const viewNavigation = (
    <Nav
      variant="tabs"
      activeKey={activeView}
      className={styles.viewNav}
      onSelect={(eventKey) => {
        if (eventKey === "payments") {
          history.push("/payments");
          return;
        }

        if (eventKey === "stripe") {
          history.push("/payments/stripe");
          return;
        }

        if (eventKey === "booking") {
          history.push("/payments/booking");
          return;
        }

        if (eventKey === "items") {
          history.push("/payments/items");
        }
      }}
    >
      <Nav.Item>
        <Nav.Link eventKey="payments">
          <FormattedMessage id="nav.payments" />
        </Nav.Link>
      </Nav.Item>
      <Nav.Item>
        <Nav.Link eventKey="stripe">
          <FormattedMessage id="payments.tabs.stripeAccount" />
        </Nav.Link>
      </Nav.Item>
      {bookingControlsEnabled ? (
        <Nav.Item>
          <Nav.Link eventKey="booking">
            <FormattedMessage id="payments.tabs.bookingSettings" />
          </Nav.Link>
        </Nav.Item>
      ) : null}
      <Nav.Item>
        <Nav.Link eventKey="items">
          <FormattedMessage id="payments.tabs.itemSettings" />
        </Nav.Link>
      </Nav.Item>
    </Nav>
  );

  return (
    <IonPage id="main-content">
      {isMobile && (
        <MobileTitle
          backButton={intl.formatMessage({ id: "common.back" })}
          id="leads.payments.pageTitle"
        />
      )}
      <IonContent
        {...(isMobile
          ? { fullscreen: true, color: "light", className: "ion-padding" }
          : {})}
      >
        <div
          className={
            isMobile
              ? "view-wrapper"
              : `page-wrapper container-fluid ${styles.pageWrapper}`
          }
        >
          {!isMobile && (
            <div>
              <h1 className={headerStyles.pageTitle}>
                <FormattedMessage id="leads.payments.pageTitle" />
              </h1>
            </div>
          )}
          {isEligible ? (
            <>
              {!shouldHideViewNavigation ? viewNavigation : null}
              {isMobile ? (
                <>
                  {activeView === "stripe" ? (
                    <div
                      className={`pt-2 px-2 pb-2 ${
                        shouldShowSettingsLanding ? styles.settingsLandingWrapper : ""
                      }`}
                    >
                      {stripeCard}
                    </div>
                  ) : activeView === "booking" ? (
                    <div className="pt-2 px-2 pb-2">{bookingSettingsCard}</div>
                  ) : activeView === "items" ? (
                    <div className="pt-2 px-2 pb-2">
                      {itemsCard}
                    </div>
                  ) : (
                    <>
                      <div className={`${styles.filtersWrapper} pt-2`}>
                        {filterPillTotal}
                        {filterPillPaid}
                      </div>
                      {isFetching && !isFetchingNextPage ? (
                        <SkeletonLoader rows={10} />
                      ) : (
                        <>
                          <List
                            handleClick={(payment: any) => {
                              openPaymentModal(payment);
                            }}
                            settings={props.settings}
                          />
                          <IonInfiniteScroll
                            disabled={!hasNextPage}
                            onIonInfinite={(event) => {
                              fetchNextPage({
                                pageParam: { direction: "next", cursor: nextCursor },
                              }).then(() => {
                                event.target.complete();
                              });
                            }}
                          >
                            <IonInfiniteScrollContent></IonInfiniteScrollContent>
                          </IonInfiniteScroll>
                        </>
                      )}
                    </>
                  )}
                  <Modal
                    isOpen={isOpen}
                    onWillDismiss={() => {
                      setIsOpen(false);
                      setPayment(null);
                    }}
                    selectedPayment={payment}
                  />
                </>
              ) : (
                <>
                  {activeView === "stripe" ? (
                    <div
                      className={`row mt-3 mb-3 ${
                        shouldShowSettingsLanding ? styles.settingsLandingWrapper : ""
                      }`}
                    >
                      <div className="col-md-12">{stripeCard}</div>
                    </div>
                  ) : activeView === "booking" ? (
                    <div className="row mt-3 mb-3">
                      <div className="col-md-12">{bookingSettingsCard}</div>
                    </div>
                  ) : activeView === "items" ? (
                    <div className="row mt-3 mb-3">
                      <div className="col-md-12">{itemsCard}</div>
                    </div>
                  ) : (
                    <>
                      <div className="row mt-3 mb-3">
                        <div className={`col-md-2`}>{filterPillTotal}</div>
                        <div className={`col-md-2`}>{filterPillPaid}</div>
                      </div>
                      <div className="flex gap-1 align-items-center justify-content-between">
                        <div className="flex gap-1 align-items-center mb-3">
                          <div className={`${styles.filters}`}>
                            <div className={styles.dateWrapper}>
                              <IconCalendar size={17} />
                              <DatePicker
                                selectsRange
                                locale={locale}
                                startDate={startDate}
                                endDate={endDate}
                                className={
                                  startDate || endDate
                                    ? styles.dateInput
                                    : styles.dateInputEmpty
                                }
                                placeholderText={intl.formatMessage({
                                  id: "leads.datePlaceholder",
                                })}
                                onChange={onChangeDates}
                                monthsShown={2}
                                popperPlacement="bottom-end"
                              />
                            </div>
                          </div>
                          {startDate && endDate ? (
                            <IonIcon
                              style={{ opacity: "50%", marginLeft: "3px" }}
                              icon={closeCircle}
                              className="pointer"
                              size="small"
                              onClick={() => {
                                setStartDate(null);
                                setEndDate(null);
                                setDateQuery("");
                              }}
                            ></IonIcon>
                          ) : null}

                          {props.settings.companies.length > 1 ? (
                            <Filter
                              options={props.settings.companies}
                              onFilterChange={(filter: string[]) =>
                                setFilters({ ...filters, companies: filter })
                              }
                              name={intl.formatMessage({ id: "common.company" })}
                              currentFilters={filters.companies}
                            ></Filter>
                          ) : null}

                          <Filter
                            options={[
                              {
                                id: "paid",
                                name: intl.formatMessage({ id: "leads.payments.paid" }),
                              },

                              {
                                id: "expired",
                                name: intl.formatMessage({
                                  id: "leads.payments.expired",
                                }),
                              },
                            ]}
                            onFilterChange={(filter: string[]) =>
                              setFilters({ ...filters, statuses: filter })
                            }
                            name={intl.formatMessage({ id: "common.status" })}
                            currentFilters={filters.companies}
                          ></Filter>
                        </div>
                        <div className="flex gap-1 align-items-center mb-3">
                          <div className={styles.btnNavigation}>
                            <button
                              disabled={exportLoading}
                              className={`btn btn-visito-light btn-sm btn-flex`}
                              onClick={() => handleExport()}
                            >
                              <IconDownload size={14} />
                              <div>
                                {exportLoading ? (
                                  <FormattedMessage id={`common.loading`} />
                                ) : (
                                  <FormattedMessage id={`leads.export`} />
                                )}
                              </div>
                            </button>{" "}
                          </div>
                        </div>
                      </div>
                      {isFetching || isFetchingNextPage ? (
                        <LayoutSkeleton />
                      ) : (
                        <div className={styles.tableContainer}>
                          <Table
                            settings={props.settings}
                            handleClick={(payment: any) => {
                              openPaymentModal(payment);
                            }}
                            onOpenConversation={(payment: any) => {
                              openConversation(payment);
                            }}
                            onTransferFunds={(payment: any) => {
                              handleTransfFunds(payment);
                            }}
                            onHydrateBalance={(payment: any) => {
                              handleHydrateBalance(payment);
                            }}
                            onOpenRefund={(payment: any) => {
                              setIsOpenRefund(true);
                              setPayment(payment);
                            }}
                            onToggleReconciled={(
                              payment: any,
                              reconciled: boolean,
                            ) => {
                              handleToggleReconciled(payment, reconciled);
                            }}
                          />
                        </div>
                      )}

                      <div className={styles.paginationWrapper}>
                        <div className="flex gap-1 justify-content-end">
                          <button
                            disabled={
                              !hasPreviousPage || isFetching || isFetchingPreviousPage
                            }
                            className="btn btn-visito-light btn-sm"
                            onClick={() =>
                              fetchNextPage({
                                pageParam: {
                                  direction: "previous",
                                  cursor: previousCursor,
                                },
                              })
                            }
                          >
                            <FormattedMessage id="common.previous" />
                          </button>
                          <button
                            disabled={
                              !hasNextPage ||
                              isFetchingNextPage ||
                              isFetchingPreviousPage
                            }
                            className="btn btn-visito-light btn-sm"
                            onClick={() =>
                              fetchNextPage({
                                pageParam: { direction: "next", cursor: nextCursor },
                              })
                            }
                          >
                            <FormattedMessage id="common.next" />
                          </button>
                        </div>
                      </div>
                      <br />
                      <br />
                      <br />
                      <br />
                    </>
                  )}
                  <RefundModal
                    isOpen={isOpenRefund}
                    onWillDismiss={() => {
                      setIsOpenRefund(false);
                      setPayment(null);
                    }}
                    onRefundFunds={handleRefundFunds}
                    selectedPayment={payment}
                  />
                  <Modal
                    isOpen={isOpen}
                    onWillDismiss={() => {
                      setIsOpen(false);
                      setPayment(null);
                    }}
                    selectedPayment={payment}
                  />
                </>
              )}
              {itemFormModal}
            </>
          ) : (
            <div />
          )}
        </div>
      </IonContent>
    </IonPage>
  );
};

interface ILayoutProps {
  settings: Settings;
}

export default Payments;
